import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { CarouselEpisodeCard, type CarouselEpisode } from './CarouselEpisodeCard';

interface EpisodeCarouselProps {
  episodes: CarouselEpisode[];
  isLoading?: boolean;
  isAuthenticated?: boolean;
  isEpisodeLiked?: (topicSlug: string, episodeId: string) => boolean;
  isEpisodePending?: (topicSlug: string, episodeId: string) => boolean;
  toggleEpisodeLike?: (topicSlug: string, episodeId: string) => void;
}

/**
 * Skeleton placeholder card for loading state.
 * Matches the dimensions of CarouselEpisodeCard.
 */
function SkeletonCard() {
  return (
    <div className="h-full overflow-hidden rounded-xl border border-gray-800 bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-4">
      <div className="flex h-full flex-col">
        {/* Podcast badge skeleton */}
        <div className="mb-3 inline-flex items-center gap-1.5">
          <div className="h-5 w-5 animate-pulse rounded bg-gray-700" />
          <div className="h-3 w-20 animate-pulse rounded bg-gray-700" />
        </div>

        {/* Episode title skeleton - 2 lines */}
        <div className="mb-2 space-y-1.5">
          <div className="h-4 w-full animate-pulse rounded bg-gray-700" />
          <div className="h-4 w-3/4 animate-pulse rounded bg-gray-700" />
        </div>

        {/* Summary skeleton - 2 lines */}
        <div className="mb-3 flex-1 space-y-1.5">
          <div className="h-3 w-full animate-pulse rounded bg-gray-700" />
          <div className="h-3 w-5/6 animate-pulse rounded bg-gray-700" />
        </div>

        {/* Bottom row: metadata and play button skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-3 w-10 animate-pulse rounded bg-gray-700" />
            <div className="h-3 w-16 animate-pulse rounded bg-gray-700" />
          </div>
          <div className="h-8 w-8 animate-pulse rounded-full bg-gray-700" />
        </div>
      </div>
    </div>
  );
}

/**
 * Episode carousel using Embla Carousel.
 * - Non-looping: newest episodes on left, oldest on right
 * - Responsive: 1 slide on mobile, 2 on tablet, 4 on desktop
 * - Navigation arrows and dot indicators
 */
export function EpisodeCarousel({
  episodes,
  isLoading = false,
  isAuthenticated = false,
  isEpisodeLiked,
  isEpisodePending,
  toggleEpisodeLike,
}: EpisodeCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    slidesToScroll: 1,
    containScroll: 'trimSnaps',
  });

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  if (episodes.length === 0 && !isLoading) {
    return null;
  }

  // Static skeleton keys to avoid array index as key
  const skeletonSlots = ['skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4'] as const;

  // Show skeleton loading state
  if (isLoading) {
    return (
      <div className="relative">
        <div className="overflow-hidden">
          <div className="flex gap-4">
            {/* Show 4 skeleton cards to match desktop view */}
            {skeletonSlots.map((id) => (
              <div
                key={id}
                className="min-w-0 flex-shrink-0 basis-full sm:basis-1/2 lg:basis-1/4"
              >
                <SkeletonCard />
              </div>
            ))}
          </div>
        </div>
        {/* Skeleton dot indicators */}
        <div className="mt-6 flex justify-center gap-2">
          {skeletonSlots.map((id, idx) => (
            <div
              key={`dot-${id}`}
              className={`h-2 rounded-full animate-pulse bg-gray-700 ${idx === 0 ? 'w-6' : 'w-2'}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Carousel viewport */}
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex gap-4">
          {episodes.map((episode) => (
            <div
              key={`${episode.topicSlug}-${episode.episodeId}`}
              className="min-w-0 flex-shrink-0 basis-full sm:basis-1/2 lg:basis-1/4"
            >
              <CarouselEpisodeCard
                episode={episode}
                isAuthenticated={isAuthenticated}
                isLiked={isEpisodeLiked?.(episode.topicSlug, episode.episodeId) ?? false}
                isPending={isEpisodePending?.(episode.topicSlug, episode.episodeId) ?? false}
                onToggleLike={() => toggleEpisodeLike?.(episode.topicSlug, episode.episodeId)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation arrows */}
      <button
        type="button"
        onClick={scrollPrev}
        disabled={!prevBtnEnabled}
        className="absolute left-0 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full border border-gray-700 bg-gray-800/90 text-gray-300 backdrop-blur-sm transition-all hover:border-gray-600 hover:bg-gray-700 hover:text-white disabled:pointer-events-none disabled:opacity-50 sm:-translate-x-1/4"
        aria-label="Previous slide"
      >
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        type="button"
        onClick={scrollNext}
        disabled={!nextBtnEnabled}
        className="absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-1/2 flex h-10 w-10 items-center justify-center rounded-full border border-gray-700 bg-gray-800/90 text-gray-300 backdrop-blur-sm transition-all hover:border-gray-600 hover:bg-gray-700 hover:text-white disabled:pointer-events-none disabled:opacity-50 sm:translate-x-1/4"
        aria-label="Next slide"
      >
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dot indicators */}
      <div className="mt-6 flex justify-center gap-2">
        {scrollSnaps.map((snap, index) => (
          <button
            key={`snap-${snap}`}
            type="button"
            onClick={() => scrollTo(index)}
            className={`h-2 w-2 rounded-full transition-all ${
              index === selectedIndex
                ? 'w-6 bg-indigo-500'
                : 'bg-gray-600 hover:bg-gray-500'
            }`}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === selectedIndex ? 'true' : 'false'}
          />
        ))}
      </div>
    </div>
  );
}
